import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Alert, Button, Dialog } from '@betareader/design-system';
import Icon from '../../components/AppIcon';
import styled from 'styled-components';
import {
  CritiqueFeedbackVisibility,
  CritiqueGroup,
  CritiqueRevealPolicy,
  useDeleteCritiqueGroup,
  useUpdateFeedbackSettings,
} from 'src/hooks/useCritiqueGroups';
import GroupCoverPanel from 'src/components/critiqueGroups/groupCoverPanel';
import VisibilityPanel from './visibilityPanel';

/**
 * Design E1 — feedback visibility settings. Host (admin) only: the API rejects
 * a moderator here, so this tab is only rendered for the host.
 */

const Panel = styled.div`
  background: var(--br-surface);
  border: 1px solid var(--br-border);
  border-radius: 14px;
  overflow: hidden;
  max-width: 620px;
`;

const Head = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid var(--br-border);
  font-weight: 800;
  font-size: 17px;
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Foot = styled.div`
  padding: 14px 24px;
  border-top: 1px solid var(--br-border);
  background: var(--br-raised-surface);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

const GroupTitle = styled.div`
  font-weight: 800;
  font-size: 14px;
  margin-bottom: 3px;
`;

const GroupHelp = styled.div`
  font-size: 12.5px;
  color: var(--br-muted);
  margin-bottom: 12px;
`;

const Option = styled.button<{ $on: boolean }>`
  box-sizing: border-box;
  width: 100%;
  border: ${({ $on }) => ($on ? '2px solid var(--br-accent)' : '1.5px solid var(--br-border)')};
  background: ${({ $on }) => ($on ? 'var(--br-raised-surface)' : 'var(--br-surface)')};
  color: var(--br-text);
  border-radius: 12px;
  padding: 13px 15px;
  text-align: left;
  font: inherit;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  &:disabled { cursor: not-allowed; opacity: 0.55; }
  &:focus-visible { outline: 2px solid var(--br-focus); outline-offset: 2px; }
`;

const Radio = styled.span<{ $on: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 4px solid ${({ $on }) => ($on ? 'var(--br-accent)' : 'var(--br-border)')};
  margin-top: 2px;
  flex-shrink: 0;
`;

const DangerPanel = styled.div`
  background: var(--br-surface);
  border: 1px solid var(--br-error-border);
  border-radius: 14px;
  overflow: hidden;
  max-width: 620px;
  margin-top: 20px;
`;

const DangerHead = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid var(--br-error-border);
  font-weight: 800;
  font-size: 17px;
  color: var(--br-error-text);
`;

const HostSettings: React.FC<{ group: CritiqueGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const save = useUpdateFeedbackSettings(group._id);
  const del = useDeleteCritiqueGroup();

  const [visibility, setVisibility] = useState<CritiqueFeedbackVisibility>(
    group.feedbackVisibility
  );
  const [reveal, setReveal] = useState<CritiqueRevealPolicy>(group.revealPolicy);
  const [saved, setSaved] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const confirmDelete = () =>
    del.mutate(
      { groupId: group._id },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          history.push('/critique-groups');
        },
      }
    );

  // Re-sync when the group refetches, so a change made elsewhere isn't
  // silently overwritten by stale local state.
  useEffect(() => {
    setVisibility(group.feedbackVisibility);
    setReveal(group.revealPolicy);
  }, [group.feedbackVisibility, group.revealPolicy]);

  const dirty =
    visibility !== group.feedbackVisibility || reveal !== group.revealPolicy;

  const submit = () => {
    setSaved(false);
    save.mutate(
      { feedbackVisibility: visibility, revealPolicy: reveal },
      { onSuccess: () => setSaved(true) }
    );
  };

  const sharedSelected = visibility === 'members_shared';

  return (
    <>
      <GroupCoverPanel group={group} />
      <VisibilityPanel group={group} />
      <Panel data-testid="host-settings">
      <Head>{t('CritiqueFeedbackVisibility', 'Feedback visibility')}</Head>
      <Body>
        <div>
          <GroupTitle>{t('CritiqueWhoSees', 'Who sees member feedback?')}</GroupTitle>
          <GroupHelp>
            {t(
              'CritiqueWhoSeesHelp',
              'Controls whether members can read each other’s comments & survey answers. You always see everything.'
            )}
          </GroupHelp>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Option
              type="button"
              aria-pressed={visibility === 'host_only'}
              $on={visibility === 'host_only'}
              onClick={() => setVisibility('host_only')}
              data-testid="settings-host-only"
            >
              <Radio $on={visibility === 'host_only'} aria-hidden="true" />
              <span>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 13.5 }}>
                  {t('CritiqueOnlyMe', 'Only me (the author)')}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--br-muted)', marginTop: 4 }}>
                  {t('CritiqueOnlyMeHelp', 'Feedback stays private to you.')}
                </span>
              </span>
            </Option>
            <Option
              type="button"
              aria-pressed={sharedSelected}
              $on={sharedSelected}
              onClick={() => setVisibility('members_shared')}
              data-testid="settings-shared"
            >
              <Radio $on={sharedSelected} aria-hidden="true" />
              <span>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 13.5 }}>
                  {t('CritiqueMembersSeeEachOther', 'Members see each other’s')}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--br-muted)', marginTop: 4 }}>
                  {t('CritiqueSharedHelp', 'A shared conversation.')}
                </span>
              </span>
            </Option>
          </div>
        </div>

        <div>
          <GroupTitle>{t('CritiqueWhenSee', 'When can they see it?')}</GroupTitle>
          <GroupHelp>
            {t(
              'CritiqueWhenSeeHelp',
              'Only applies when members can see each other’s feedback.'
            )}
          </GroupHelp>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Option
              type="button"
              aria-pressed={reveal === 'immediate'}
              $on={reveal === 'immediate'}
              disabled={!sharedSelected}
              onClick={() => sharedSelected && setReveal('immediate')}
              data-testid="settings-immediate"
            >
              <Radio $on={reveal === 'immediate'} aria-hidden="true" />
              <span style={{ fontWeight: 700, fontSize: 13.5 }}>
                {t('CritiqueRevealImmediate', 'Immediately')}
              </span>
            </Option>
            <Option
              type="button"
              aria-pressed={reveal === 'after_own'}
              $on={reveal === 'after_own'}
              disabled={!sharedSelected}
              onClick={() => sharedSelected && setReveal('after_own')}
              data-testid="settings-after-own"
            >
              <Radio $on={reveal === 'after_own'} aria-hidden="true" />
              <span>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 13.5 }}>
                  {t('CritiqueRevealAfterOwn', 'Only after they’ve shared their own')}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--br-accent)', marginTop: 4 }}>
                  {t(
                    'CritiqueRevealAfterOwnHelp',
                    'Reduces anchoring — members form an honest first impression before seeing others.'
                  )}
                </span>
              </span>
            </Option>
          </div>
        </div>

        {save.isError && (
          <Alert variant="error" data-testid="settings-error">
            {save.error?.message}
          </Alert>
        )}
      </Body>
      <Foot>
        {saved && !dirty && (
          <span role="status" style={{ fontSize: 12.5, color: 'var(--br-success-text)', fontWeight: 700 }}>
            {t('CritiqueSettingsSaved', 'Saved')}
          </span>
        )}
        <Button
          onClick={submit}
          disabled={!dirty || save.isPending}
          aria-busy={save.isPending}
          style={{ borderRadius: 9, fontWeight: 700 }}
          data-testid="settings-save"
        >
          {t('CritiqueSaveSettings', 'Save settings')}
        </Button>
      </Foot>
      </Panel>

      <DangerPanel data-testid="host-danger-zone">
        <DangerHead>{t('CritiqueDangerZone', 'Danger zone')}</DangerHead>
        <Body>
          <div>
            <GroupTitle>{t('CritiqueDeleteGroup', 'Delete this group')}</GroupTitle>
            <GroupHelp>
              {t(
                'CritiqueDeleteGroupHelp',
                'Permanently removes the group, its discussion and every membership, and revokes members’ access to the linked book. This can’t be undone.'
              )}
            </GroupHelp>
            {del.isError && (
              <Alert variant="error" data-testid="delete-error">
                {del.error?.message}
              </Alert>
            )}
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(true)}
              style={{ borderRadius: 9, fontWeight: 700, color: 'var(--br-error-text)' }}
              data-testid="delete-group-button"
            >
              <Icon name="trash" aria-hidden="true" />
              {t('CritiqueDeleteGroup', 'Delete this group')}
            </Button>
          </div>
        </Body>
      </DangerPanel>

      <Dialog
        open={confirmOpen}
        role="alertdialog"
        onClose={() => { if (!del.isPending) setConfirmOpen(false); }}
        title={t('CritiqueDeleteConfirmTitle', 'Delete “{{name}}”?', {
          name: group.name,
        })}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}
              disabled={del.isPending} data-testid="delete-cancel">
              {t('Cancel', 'Cancel')}
            </Button>
            <Button variant="danger" onClick={confirmDelete}
              aria-busy={del.isPending} disabled={del.isPending}
              data-testid="delete-confirm">
              {t('CritiqueDeleteConfirmYes', 'Delete group')}
            </Button>
          </>
        }
      >
        {t(
          'CritiqueDeleteConfirmBody',
          'This permanently removes the group and everyone’s access to it. This can’t be undone.'
        )}
      </Dialog>
    </>
  );
};

export default HostSettings;
